import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ThumbsUp, ThumbsDown, Check } from "lucide-react";
import { issuesApi } from "@/api/issues";

/**
 * 👍/👎 + comentário opcional num entregável. Diferente de aprovar/reprovar
 * (que é o gate do fluxo): isso é captura de sentimento — vale até num
 * entregável já aprovado. Alimenta o loop de aprendizado e dá feedback
 * qualitativo dos usuários.
 *
 * Aponta pra última revisão do documento principal da issue (targetType
 * "issue_document_revision"). Se a issue não tem documento (ex: tarefas de
 * arte), não renderiza nada.
 */
export function FeedbackWidget({ issueId }: { issueId: string }) {
  const queryClient = useQueryClient();
  const [showComment, setShowComment] = useState(false);
  const [comment, setComment] = useState("");

  const revisionsQuery = useQuery({
    queryKey: ["issues", issueId, "document-revisions"],
    queryFn: () => issuesApi.documentRevisions(issueId),
    enabled: !!issueId,
  });
  const votesQuery = useQuery({
    queryKey: ["issues", issueId, "feedback-votes"],
    queryFn: () => issuesApi.feedbackVotes(issueId),
    enabled: !!issueId,
  });

  // Última revisão = o alvo do voto.
  const latestRevision = (revisionsQuery.data ?? [])
    .slice()
    .sort((a, b) => b.revisionNumber - a.revisionNumber)[0];
  const targetId = latestRevision?.id ?? null;
  const myVote = (votesQuery.data ?? []).find(
    (v) => v.targetType === "issue_document_revision" && v.targetId === targetId,
  );

  const save = useMutation({
    mutationFn: (input: { vote: "up" | "down"; reason?: string }) => {
      if (!targetId) throw new Error("sem revisão de documento");
      return issuesApi.saveFeedbackVote(issueId, {
        targetType: "issue_document_revision",
        targetId,
        vote: input.vote,
        reason: input.reason,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["issues", issueId, "feedback-votes"] });
    },
  });

  // Sem documento → nada a avaliar (tarefas de arte etc).
  if (!revisionsQuery.isLoading && !targetId) return null;
  if (!targetId) return null;

  const vote = (v: "up" | "down") => {
    // Se já votei isso, mantém; senão troca. Em ambos os casos reabre o
    // campo de comentário (deixa a pessoa explicar).
    save.mutate({ vote: v, reason: myVote?.reason ?? undefined });
    setComment(myVote?.reason ?? "");
    setShowComment(true);
  };

  const btn = (v: "up" | "down", Icon: typeof ThumbsUp, label: string) => {
    const active = myVote?.vote === v;
    const accent = v === "up" ? "#3d7d3d" : "var(--nd-orange-pun)";
    return (
      <button
        type="button"
        onClick={() => vote(v)}
        disabled={save.isPending}
        title={label}
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 6,
          padding: "6px 12px",
          borderRadius: 8,
          border: "1px solid",
          borderColor: active ? accent : "var(--wf-ink-5)",
          background: active ? accent : "var(--wf-paper)",
          color: active ? "#fff" : "var(--wf-ink-2)",
          fontSize: 12,
          fontWeight: 600,
          fontFamily: "var(--wf-font-body)",
          cursor: save.isPending ? "default" : "pointer",
          transition: "all 120ms",
        }}
      >
        <Icon size={13} strokeWidth={1.9} /> {label}
      </button>
    );
  };

  return (
    <div
      className="no-print"
      style={{
        marginTop: 28,
        paddingTop: 18,
        borderTop: "1px solid var(--wf-ink-5)",
        display: "flex",
        flexDirection: "column",
        gap: 10,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
        <span style={{ fontSize: 13, fontWeight: 600, color: "var(--wf-ink-2)" }}>
          {myVote ? "Obrigado pelo feedback." : "Esse entregável ficou bom?"}
        </span>
        {btn("up", ThumbsUp, "Ficou bom")}
        {btn("down", ThumbsDown, "Pode melhorar")}
        {myVote && !showComment && (
          <button
            type="button"
            onClick={() => {
              setComment(myVote.reason ?? "");
              setShowComment(true);
            }}
            style={{
              background: "none",
              border: "none",
              cursor: "pointer",
              fontSize: 12,
              color: "var(--nd-orange-pun)",
              fontWeight: 600,
              fontFamily: "var(--wf-font-body)",
            }}
          >
            {myVote.reason ? "editar comentário" : "adicionar comentário"}
          </button>
        )}
      </div>

      {showComment && (
        <div style={{ display: "flex", flexDirection: "column", gap: 8, maxWidth: 560 }}>
          <textarea
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder="O que pesou? (opcional) — ex: tom ficou formal demais, faltou puxar pro nosso diferencial, a estrutura ajudou..."
            rows={3}
            style={{
              width: "100%",
              padding: 10,
              border: "1px solid var(--wf-ink-5)",
              borderRadius: 8,
              fontFamily: "var(--wf-font-body)",
              fontSize: 13,
              resize: "vertical",
              background: "var(--wf-paper)",
              lineHeight: 1.5,
            }}
          />
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <button
              type="button"
              className="wf-btn wf-btn--primary wf-btn--sm"
              disabled={save.isPending || !myVote}
              onClick={() => {
                if (!myVote) return;
                save.mutate({ vote: myVote.vote, reason: comment.trim() || undefined });
                setShowComment(false);
              }}
              style={{ display: "inline-flex", alignItems: "center", gap: 5 }}
            >
              <Check size={12} color="#fff" /> {save.isPending ? "salvando…" : "Salvar comentário"}
            </button>
            <button
              type="button"
              className="wf-btn wf-btn--ghost wf-btn--sm"
              onClick={() => setShowComment(false)}
            >
              Fechar
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
